/**
 * 支付相关场景适配（paywall）
 *
 * 触发：支付失败 / 取消回跳（及后续其它支付结果点），非冷启、非付费墙 mount/unmount。
 * 流程：ResolveOffersV2 → transform → upsert session / enqueue →
 *       若当前 activeScene 可展示则 tryShow；否则（如 reader → null）只落 store，
 *       等跳页后由 SceneSync.onScene 再展示。
 * 禁止抢写 activeScene（回跳后真相源归路由 / SceneSync）。
 */

import { ActivityApi } from '@/apis/activity';
import { useActivityQueueStore } from '@/store/activity-queue';
import { useActivitySessionStore } from '@/store/activity-session';

import { transformOfferResolve } from '../transform-offer';

/** 支付挽留 ResolveOffers 结果：用于「有弹窗则不出取消/失败 toast」 */
export interface IPaywallOfferResult {
    /** 是否写入过 task / session */
    wrote: boolean;
    /** 是否当场 tryShow 成功（弹窗已进 current） */
    shown: boolean;
    /**
     * 应跳过取消/失败 toast。
     * true：当场弹出挽留、队列已有弹窗占位、或同单已处理。
     * false：无可见弹窗，可 toast（含只落队列 / 名额用尽 / resolve 空）。
     */
    suppressToast: boolean;
}

/** 同单防重：按已处理压 toast，避免弹窗已开时再出取消提示 */
const DUP_SUPPRESS_TOAST: IPaywallOfferResult = { wrote: false, shown: true, suppressToast: true };

/** 同一订单只触发一次挽留 resolve，避免 cancel 多路径重复入队 */
const retentionResolvedOrderIds = new Set<string>();

/** 登录态切换时清空支付挽留订单防重记录。 */
export function resetPaywallOfferResolvedOrders(): void {
    retentionResolvedOrderIds.clear();
}

/**
 * 组装结果：有 current 或本次 shown 时压 toast，避免与活动弹窗叠放。
 * @param wrote 是否写入过 task/session
 * @param shown 是否当场 tryShow 成功
 */
function buildOfferResult(wrote: boolean, shown: boolean): IPaywallOfferResult {
    const hasPopup = shown || Boolean(useActivityQueueStore.getState().current);
    return { wrote, shown, suppressToast: hasPopup };
}

/**
 * 支付相关 ResolveOffers：入队 / 写 session；有可信 activeScene 时才 tryShow。
 * 当前仅支付失败/取消（exit）场景调用；open 进墙拉取预留后续能力。
 * @param unpaidOrderId 本次未支付的 commerce 订单 ID；非空时透传给活动接口
 * @returns wrote / shown / suppressToast；调用方用 suppressToast 决定是否打取消/失败 toast
 */
export async function runPaywallOffer(unpaidOrderId?: string): Promise<IPaywallOfferResult> {
    try {
        const res = await ActivityApi.resolveOffersV2({
            scene: 'paywall',
            event: '',
            action: 'activate',
            ...(unpaidOrderId ? { unpaid_order_id: unpaidOrderId } : {}),
        });
        const { items } = transformOfferResolve(res?.data, { scene: 'paywall' });

        let wrote = false;

        items.forEach(item => {
            if (item.session) {
                useActivitySessionStore.getState().upsertSession(item.session);
                wrote = true;
            }
            if (item.task) {
                useActivityQueueStore.getState().enqueue(item.task);
                wrote = true;
            }
        });

        if (!wrote) {
            return buildOfferResult(false, false);
        }

        // 返回后再读 scene，避免回跳改 URL 与 resolve 竞态
        const { activeScene, tryShow } = useActivityQueueStore.getState();
        if (!activeScene) {
            // reader 等非可展示页：只落 store，跳页后由 onScene 续展；无 current 时允许 toast
            return buildOfferResult(true, false);
        }

        // 只 tryShow，不 resetEntry / force。
        // 【口径】整会话只自动弹 1 次（含挽留）：冷启已弹过则 shown=false，任务留在 waiting，属预期。
        const shown = tryShow(activeScene);
        return buildOfferResult(true, shown);
    } catch (error) {
        console.error('[runPaywallOffer] resolveOffersV2 failed', error);
        return buildOfferResult(false, false);
    }
}

/**
 * 支付失败 / 取消后拉取支付 offer（event=exit）。
 * @param orderId 可选；传入时按订单防重
 * @returns wrote / shown / suppressToast；suppressToast=true 时调用方应跳过取消/失败 toast
 */
export function runPaywallOfferAfterPaymentAbandon(orderId?: string): Promise<IPaywallOfferResult> {
    const id = orderId?.trim();
    if (id) {
        if (retentionResolvedOrderIds.has(id)) {
            return Promise.resolve(DUP_SUPPRESS_TOAST);
        }
        retentionResolvedOrderIds.add(id);
    }
    return runPaywallOffer(id);
}
